import { useEffect, useState } from "react";
import Modal from "./Modal";
import Input from "./Input";
import Button from "./Button";

export interface RenameModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  initialValue: string;
  inputLabel?: string;
  placeholder?: string;
  maxLength?: number;
  saveLabel?: string;
  loading?: boolean;
  /** Return an error message to block save, or null/undefined if valid. */
  validate?: (trimmed: string) => string | null | undefined;
  onSave: (trimmed: string) => void;
}

/**
 * Generic rename dialog: text field + save/cancel. Resets draft when opened.
 */
export default function RenameModal({
  isOpen,
  onClose,
  title,
  subtitle,
  initialValue,
  inputLabel = "Name",
  placeholder,
  maxLength = 120,
  saveLabel = "Save",
  loading = false,
  validate,
  onSave,
}: RenameModalProps) {
  const [value, setValue] = useState(initialValue);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setValue(initialValue);
      setLocalError(null);
    }
  }, [isOpen, initialValue]);

  const handleSave = () => {
    const trimmed = value.trim();
    const err = validate?.(trimmed);
    if (err) {
      setLocalError(err);
      return;
    }
    setLocalError(null);
    onSave(trimmed);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      title={title}
      subtitle={subtitle}
      onCloseModal={onClose}
      size="sm"
      height="auto"
      maxHeight="90vh"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={loading}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            loading={loading}
            disabled={loading}
            onClick={handleSave}
          >
            {saveLabel}
          </Button>
        </div>
      }
    >
      <div className="py-2 space-y-2">
        <Input
          id="rename-modal-input"
          label={inputLabel}
          value={value}
          maxLength={maxLength}
          placeholder={placeholder}
          disabled={loading}
          onChange={(e) => setValue(e.target.value)}
        />
        {localError && (
          <p className="text-sm text-salmon-400" role="alert">
            {localError}
          </p>
        )}
      </div>
    </Modal>
  );
}
